/**
 * Go To — the bar's one box: open anything in ProDoc by clicking, typing, or
 * picking from the list. Alt + G (or Ctrl/⌘ + K) opens it from any screen.
 *
 * It takes the place of the bar's document search and keeps it: typing finds
 * documents by name (the register's own search), and *Search every document*
 * opens the register with the query. Around that it lists what used to need
 * knowing where to click — pages, actions, views of the register by workspace,
 * **saved views**, and, as you type, **folders** and **tags** by name (W5,
 * FR-17) — other organisations and the FiveOX screens — **only what this
 * person can open**, which the footer says rather than showing locked rows.
 *
 * The keyboard never leaves the box: ↑/↓ move, Enter opens, Ctrl/⌘ + Enter
 * opens in a new tab, Shift + Enter pins. A side panel says what the
 * highlighted item is, whose it is, and what this person may do there.
 *
 * The palette renders inside the kit's `Dialog` — the overlay, the focus trap,
 * Escape and the focus return are the kit's, not a second implementation
 * (UI_SYSTEM; the prototype hand-rolled this one). It is the app's own
 * component, in shape (b) of plan §4.10, and a candidate for `@fiveox/shell`
 * (VENDORED.md) once every app wants the same slot.
 */
import { StatusBadge, mediaLabel, type ProDocDocument, type Tag, type Workspace } from '@fiveox/prodoc-ui'
import { Button, Dialog, cn, formatDate, formatDateTime } from '@fiveox/ui'
import {
  Building2,
  ChevronRight,
  CloudUpload,
  ExternalLink,
  FileText,
  Folder as FolderIcon,
  ListFilter,
  Plus,
  Search,
  Star,
  Tag as TagIcon,
  type LucideIcon,
} from 'lucide-react'
import { useCallback, useEffect, useId, useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'

import { FOLDER, TAG, type Verb } from '../lib/access'
import { errorMessage, prodoc } from '../lib/api'
import { ACCOUNTS_URL, CONSOLE_URL } from '../lib/config'
import {
  KIND_LABEL,
  SECTION_TITLE,
  matches,
  rank,
  readHistory,
  recordVisit,
  remember,
  removeFilter,
  staticItems,
  togglePin,
  type FolderRef,
  type GoToItem,
  type GoToKind,
  type GoToSection,
  type Remembered,
} from '../lib/goto'

const KIND_ICON: Record<GoToKind, LucideIcon> = {
  page: ChevronRight,
  action: Plus,
  view: ListFilter,
  document: FileText,
  folder: FolderIcon,
  tag: TagIcon,
  organisation: Building2,
  platform: ExternalLink,
}
const ITEM_ICON: Record<string, LucideIcon> = {
  'action:upload': CloudUpload,
}
const iconFor = (item: GoToItem) => (item.id.startsWith('search:') ? Search : (ITEM_ICON[item.id] ?? KIND_ICON[item.kind]))

const isMac = typeof navigator !== 'undefined' && /mac/i.test(navigator.platform)
const MOD = isMac ? '⌘' : 'Ctrl'

/** A document row from the search, plus its facts for the side panel. */
type Row = GoToItem & { row?: ProDocDocument }

export interface GoToProps {
  userId: string | null
  org: { id: string; display_name: string }
  orgs: { id: string; display_name: string }[]
  can: (resource: string, verb: Verb) => boolean
  onSwitchOrg: (orgId: string) => void
  /** W22 (FR-59): opens the upload popup over the current screen — the host's `openUpload`. */
  onUpload?: () => void
}

/** How many workspaces' folder trees a search reads — the folders of the rest are a register's tree away. */
const FOLDER_WORKSPACES = 10

/** The bar's trigger, and the palette it opens. */
export function GoTo(props: GoToProps) {
  const [open, setOpen] = useState(false)
  const [workspaces, setWorkspaces] = useState<Workspace[] | null>(null)
  const [tags, setTags] = useState<Tag[] | null>(null)
  const [folders, setFolders] = useState<FolderRef[] | null>(null)
  const [foldersAsked, setFoldersAsked] = useState(false)

  // The views come from the workspace list and the tags: refreshed on every
  // opening, so they are current, and kept between openings, so a second
  // opening shows them at once. The list is usable before they arrive.
  useEffect(() => {
    if (!open) return
    setFoldersAsked(false)
    void prodoc.workspaces
      .list()
      .then(setWorkspaces)
      .catch(() => {})
    if (props.can(TAG, 'can_view')) {
      void prodoc.tags
        .list()
        .then(setTags)
        .catch(() => {})
    }
    // Opening is the question; the verbs are read as they are then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Folders answer a search only, so their trees are read the first time an
  // opening is searched — never just because the palette opened.
  const loadFolders = useCallback(() => {
    if (foldersAsked || !workspaces || !props.can(FOLDER, 'can_view')) return
    setFoldersAsked(true)
    const live = workspaces.filter((w) => w.status !== 'archived').slice(0, FOLDER_WORKSPACES)
    void Promise.all(live.map((w) => prodoc.folders.tree(w.id).then((list) => list.map((folder) => ({ folder, workspace: { id: w.id, name: w.name } })), () => [])))
      .then((trees) => setFolders(trees.flat()))
      .catch(() => {})
  }, [foldersAsked, workspaces, props.can])

  // From anywhere: Alt + G, or the Ctrl/⌘ + K most tools use.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const altG = e.altKey && !e.ctrlKey && !e.metaKey && e.code === 'KeyG'
      const modK = (e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === 'k'
      if (!altG && !modK) return
      e.preventDefault()
      setOpen((o) => !o)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-keyshortcuts="Alt+G Control+K"
        aria-label="Go To (Alt+G)"
        className="text-muted-foreground w-72 justify-start gap-2 font-normal xl:w-80"
      >
        <Search size={15} className="shrink-0" aria-hidden />
        <span className="min-w-0 flex-1 truncate text-left">Go To — pages, documents, workspaces…</span>
        <Kbd>Alt+G</Kbd>
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} title="Go To" width="xl">
        {open && <Palette {...props} workspaces={workspaces} tags={tags} folders={folders} onSearch={loadFolders} onClose={() => setOpen(false)} />}
      </Dialog>
    </>
  )
}

function Palette({
  userId,
  org,
  orgs,
  can,
  onSwitchOrg,
  onUpload,
  workspaces,
  tags,
  folders,
  onSearch,
  onClose,
}: GoToProps & { workspaces: Workspace[] | null; tags: Tag[] | null; folders: FolderRef[] | null; onSearch: () => void; onClose: () => void }) {
  const navigate = useNavigate()
  const baseId = useId()
  const [query, setQuery] = useState('')
  const [activeKey, setActiveKey] = useState<string | null>(null)
  const [docs, setDocs] = useState<{ q: string; rows: ProDocDocument[]; total: number; error?: string } | null>(null)
  const [historyVersion, setHistoryVersion] = useState(0)

  const close = useCallback(() => onClose(), [onClose])

  // Documents, by name — the register's own search.
  const q = query.trim()
  useEffect(() => {
    if (q.length >= 1) onSearch()
  }, [q, onSearch])
  useEffect(() => {
    if (q.length < 2) {
      setDocs(null)
      return
    }
    let current = true
    const timer = setTimeout(() => {
      void prodoc.documents
        .list({ q, status: 'stored', limit: 5 })
        .then((page) => current && setDocs({ q, rows: page.items, total: page.total }))
        .catch((e) => current && setDocs({ q, rows: [], total: 0, error: errorMessage(e) }))
    }, 180)
    return () => {
      current = false
      clearTimeout(timer)
    }
  }, [q])

  // eslint-disable-next-line react-hooks/exhaustive-deps -- re-read after a pin or a forgotten view
  const history = useMemo(() => readHistory(userId, org.id), [userId, org.id, historyVersion])

  const base = useMemo(
    () =>
      staticItems({
        can,
        otherOrgs: orgs.filter((o) => o.id !== org.id).map((o) => ({ id: o.id, name: o.display_name })),
        workspaces,
        folders,
        tags,
        saved: history.saved,
        consoleUrl: CONSOLE_URL,
        accountsUrl: ACCOUNTS_URL,
        onSwitchOrg,
        onUpload,
      }),
    [can, orgs, org.id, workspaces, folders, tags, history.saved, onSwitchOrg, onUpload],
  )
  const pinnedIds = new Set(history.pins.map((p) => p.id))

  const groups = useMemo(() => {
    const byId = new Map(base.map((i) => [i.id, i]))
    // A remembered item comes back as the live one where it still exists (its
    // counts current, and gone if access to it went); a document as it was.
    const revive = (r: Remembered, section: GoToSection): Row | null => {
      const opened = r.at ? `opened ${formatDateTime(r.at)}` : undefined
      const live = byId.get(r.id)
      if (live) return { ...live, section, hint: section === 'recent' && opened ? `· ${opened}` : live.hint, when: opened ?? live.when }
      if (r.kind !== 'document' || !r.to) return null
      return {
        id: r.id,
        kind: 'document',
        section,
        label: r.label,
        hint: section === 'recent' && opened ? `· ${opened}` : undefined,
        meta: r.meta,
        to: r.to,
        about: r.about ?? 'A document in this organisation.',
        may: 'open',
        when: opened,
        pinnable: true,
      }
    }

    const seen = new Set<string>()
    const out: { section: GoToSection; items: Row[] }[] = []
    const add = (section: GoToSection, list: Row[], filter = true) => {
      const kept = list
        .filter((i) => !seen.has(i.id) && (!filter || matches(i, q)))
        .map((item, order) => ({ item, order, score: rank(item, q) }))
        .sort((a, b) => (q ? a.score - b.score : 0) || a.order - b.order)
        .map((x) => x.item)
      for (const i of kept) seen.add(i.id)
      if (kept.length) out.push({ section, items: kept })
    }
    const of = (section: GoToSection) => base.filter((i) => i.section === section)

    add('pinned', history.pins.map((r) => revive(r, 'pinned')).filter((r): r is Row => Boolean(r)))
    add(
      'recent',
      history.recent
        .map((r) => revive(r, 'recent'))
        .filter((r): r is Row => Boolean(r))
        .slice(0, q ? 6 : 4),
    )
    add('saved', of('saved'))
    add('actions', of('actions'))
    add('pages', of('pages'))
    add('views', of('views'))
    // Folders and tags answer a search: listed in full they would bury everything else.
    if (q.length >= 1) {
      add('folders', of('folders').slice(0, 200))
      add('tags', of('tags'))
    }
    // Documents only answer a search: listed in full they would bury everything else.
    if (q.length >= 2 && can('document', 'can_view')) {
      const ready = docs?.q === q ? docs : null
      const searchAll: Row = {
        id: `search:${q}`,
        kind: 'action',
        section: 'documents',
        label: `Search every document for “${q}”`,
        meta: ready ? (ready.error ? 'could not search' : ready.total ? `${ready.total} ${ready.total === 1 ? 'match' : 'matches'}` : 'no matches') : 'searching…',
        to: `/documents?q=${encodeURIComponent(q)}`,
        about: 'Opens the register with every document whose title or file name contains this — the same search as the register’s own box.',
        may: 'open',
        pinnable: false,
      }
      // Once the search has answered with nothing, offering it would only open
      // an empty register; the list says "Nothing matches" instead.
      const offerSearch = !ready || ready.total > 0 || Boolean(ready.error)
      add('documents', [...(offerSearch ? [searchAll] : []), ...(ready?.rows ?? []).map(documentItem)], false)
    }
    add('organisation', of('organisation'))
    return out
  }, [base, history, q, docs, can])

  const flat = groups.flatMap((g) => g.items)
  // The highlight is held by **item**, not by row: counts and documents arrive
  // while the person types, and a row number would slide the highlight onto
  // something else just as Enter is pressed.
  const keyOf = (item: Row) => `${item.section}:${item.id}`
  const found = activeKey ? flat.findIndex((item) => keyOf(item) === activeKey) : -1
  const activeIndex = Math.max(found, 0)
  const current = flat[activeIndex]
  const optionId = (i: number) => `${baseId}-opt-${i}`

  // A new query starts at the top; once there, the highlight stays put.
  useEffect(() => setActiveKey(null), [q])
  useEffect(() => {
    if (found < 0 && flat[0]) setActiveKey(keyOf(flat[0]))
  })
  // The highlighted row stays in view as the arrows move it.
  useEffect(() => {
    document.getElementById(optionId(activeIndex))?.scrollIntoView({ block: 'nearest' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex])

  const openItem = (item: Row | undefined, newTab: boolean) => {
    if (!item) return
    if (item.run) {
      close()
      item.run()
      return
    }
    // Screens record themselves in Recent when they open; another app
    // cannot, so it is recorded here.
    if (item.href) {
      recordVisit(userId, org.id, remember(item))
      close()
      if (newTab) window.open(item.href, '_blank', 'noopener')
      else window.location.href = item.href
      return
    }
    if (!item.to) return
    close()
    if (newTab) window.open(item.to, '_blank', 'noopener')
    else navigate(item.to)
  }

  const pin = (item: Row | undefined) => {
    if (!item?.pinnable) return
    togglePin(userId, org.id, remember(item))
    setHistoryVersion((v) => v + 1)
  }

  const forget = (item: Row) => {
    removeFilter(userId, org.id, item.id)
    setHistoryVersion((v) => v + 1)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      if (!flat.length) return
      const step = e.key === 'ArrowDown' ? 1 : -1
      setActiveKey(keyOf(flat[(activeIndex + step + flat.length) % flat.length]!))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (e.shiftKey) pin(current)
      else openItem(current, e.ctrlKey || e.metaKey)
    }
  }

  let index = -1
  return (
    <div className="-mx-1 flex max-h-[70vh] flex-col">
      {/* The box. The kit's `Input` is a labelled form field; a combobox with
          aria-activedescendant is the one control it does not model, so the
          palette keeps its own, styled with the same tokens. */}
      <div className="border-subtle flex shrink-0 items-center gap-3 border-b px-1 pb-3">
        <input
          role="combobox"
          aria-expanded="true"
          aria-controls={`${baseId}-list`}
          aria-activedescendant={current ? optionId(activeIndex) : undefined}
          aria-autocomplete="list"
          aria-label="Go To — type what you want to open or do"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Type what you want to open or do — a page, a document, a workspace…"
          className="border-input focus:border-primary focus:ring-primary/20 h-10 min-w-0 flex-1 rounded-lg border bg-white px-3 text-sm focus:ring-2 focus:outline-none"
          autoComplete="off"
          spellCheck={false}
          autoFocus
        />
        <Kbd>Esc</Kbd>
      </div>

      <div className="flex min-h-0 flex-1">
        {/* The list. */}
        <div id={`${baseId}-list`} role="listbox" aria-label="Results" className="min-h-0 flex-1 overflow-y-auto py-2">
          {flat.length === 0 ? (
            <div className="px-6 py-10 text-center">
              <p className="text-foreground font-medium">Nothing matches “{q}”</p>
              <p className="text-muted-foreground mt-1 text-sm">Try fewer words or part of a file name. Only what you can open is listed.</p>
            </div>
          ) : (
            groups.map((group) => (
              <div key={group.section} role="group" aria-labelledby={`${baseId}-${group.section}`}>
                <div id={`${baseId}-${group.section}`} className="text-muted-foreground px-4 pt-3 pb-1 text-xs font-semibold tracking-wide uppercase">
                  {SECTION_TITLE[group.section]}
                </div>
                {group.items.map((item) => {
                  index += 1
                  const i = index
                  const Icon = iconFor(item)
                  const isActive = i === activeIndex
                  const pinned = pinnedIds.has(item.id)
                  return (
                    <div
                      key={`${group.section}-${item.id}`}
                      id={optionId(i)}
                      role="option"
                      aria-selected={isActive}
                      // Keep the caret in the box: a click opens, it does not focus.
                      onMouseDown={(e) => e.preventDefault()}
                      onMouseMove={() => i !== activeIndex && setActiveKey(keyOf(item))}
                      onClick={(e) => openItem(item, e.ctrlKey || e.metaKey)}
                      className={cn('flex cursor-pointer items-center gap-3 px-4 py-2 text-sm', isActive ? 'bg-primary/10' : 'hover:bg-muted/60')}
                    >
                      <span
                        className={cn(
                          'flex h-7 w-7 shrink-0 items-center justify-center rounded-md',
                          isActive ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
                        )}
                        aria-hidden
                      >
                        <Icon size={15} strokeWidth={1.75} />
                      </span>
                      <span className="min-w-0 flex-1 truncate">
                        <span className="text-foreground font-medium">{item.label}</span>
                        {item.hint && <span className="text-muted-foreground ml-1.5 text-xs">{item.hint}</span>}
                      </span>
                      {item.meta && <span className="text-muted-foreground hidden max-w-[11rem] shrink-0 truncate text-xs sm:block">{item.meta}</span>}
                      <span className="text-muted-foreground hidden w-20 shrink-0 text-right text-xs sm:block">{KIND_LABEL[item.kind]}</span>
                      {item.pinnable ? (
                        // Mouse only; the keyboard pins with Shift + Enter (in the footer).
                        <span
                          aria-hidden
                          title={pinned ? 'Unpin' : 'Pin to the top'}
                          onClick={(e) => {
                            e.stopPropagation()
                            pin(item)
                          }}
                          className={cn('shrink-0 rounded p-1 transition-colors', pinned ? 'text-primary' : 'text-muted-foreground/40 hover:text-foreground')}
                        >
                          <Star size={14} fill={pinned ? 'currentColor' : 'none'} />
                        </span>
                      ) : (
                        <span className="w-[22px] shrink-0" aria-hidden />
                      )}
                    </div>
                  )
                })}
              </div>
            ))
          )}
        </div>

        {/* What the highlighted item is. */}
        <aside className="border-subtle bg-muted/30 hidden w-72 shrink-0 overflow-y-auto border-l p-5 md:block" aria-label="About the highlighted item">
          {current && <Details item={current} orgName={org.display_name} pinned={pinnedIds.has(current.id)} onForget={current.section === 'saved' ? () => forget(current) : undefined} />}
        </aside>
      </div>

      <div className="border-subtle text-muted-foreground flex shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t px-1 pt-2 text-xs">
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span>
            <Kbd>↑</Kbd> <Kbd>↓</Kbd> move
          </span>
          <span>
            <Kbd>Enter</Kbd> open
          </span>
          <span className="max-sm:hidden">
            <Kbd>{MOD}+Enter</Kbd> new tab
          </span>
          <span className="max-sm:hidden">
            <Kbd>Shift+Enter</Kbd> pin
          </span>
        </span>
        <span>
          {flat.length} result{flat.length === 1 ? '' : 's'} · only what you can open is listed
        </span>
      </div>
    </div>
  )
}

/** The side panel: what it is, whose, when, and what this person may do. */
function Details({ item, orgName, pinned, onForget }: { item: Row; orgName: string; pinned: boolean; onForget?: () => void }) {
  const kind = KIND_LABEL[item.kind]
  return (
    <div>
      <p className="text-foreground font-semibold">{item.label}</p>
      <dl className="mt-3 grid grid-cols-[5rem_1fr] gap-x-3 gap-y-1.5 text-sm">
        <dt className="text-muted-foreground">What</dt>
        <dd className="text-foreground">{kind.charAt(0).toUpperCase() + kind.slice(1)}</dd>
        <dt className="text-muted-foreground">Whose</dt>
        <dd className="text-foreground">{item.kind === 'platform' ? 'FiveOX — every app' : orgName}</dd>
        {item.when && (
          <>
            <dt className="text-muted-foreground">When</dt>
            <dd className="text-foreground">{item.when}</dd>
          </>
        )}
        <dt className="text-muted-foreground">You may</dt>
        <dd className="text-foreground">{item.may}</dd>
      </dl>
      {item.row && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <StatusBadge status={item.row.status} size="sm" />
          <span className="text-muted-foreground text-xs">{mediaLabel(item.row.media_type)}</span>
        </div>
      )}
      <p className="text-muted-foreground mt-4 text-sm">{item.about}</p>
      {pinned && <p className="text-primary mt-4 text-xs font-medium">Pinned — Shift + Enter to unpin.</p>}
      {onForget && (
        <Button variant="link" size="sm" className="mt-3 px-0" onMouseDown={(e) => e.preventDefault()} onClick={onForget}>
          Forget this saved view
        </Button>
      )}
    </div>
  )
}

/** A document from the search, as a Go To item. */
function documentItem(r: ProDocDocument): Row {
  return {
    id: `doc:${r.id}`,
    kind: 'document',
    section: 'documents',
    label: r.title,
    meta: [r.owner_app !== 'prodoc' ? r.owner_app : null, mediaLabel(r.media_type)].filter(Boolean).join(' · '),
    to: `/documents/${r.id}`,
    about: [r.title !== r.file_name ? `The file ${r.file_name}.` : null, r.source_ref ? `${r.owner_app}'s record ${r.source_ref}.` : null]
      .filter(Boolean)
      .join(' ') || 'A document in this organisation.',
    may: 'open and download',
    when: `uploaded ${formatDate(r.created_at)}`,
    pinnable: true,
    row: r,
  }
}

function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="border-subtle text-muted-foreground inline-flex shrink-0 items-center rounded border bg-white px-1.5 py-0.5 font-sans text-[11px] leading-none font-medium">
      {children}
    </kbd>
  )
}
